import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { USER_ROLE } from '@/config/constants';
import {
  useAssignAgentToContractMutation,
  useGetAgentsByAdminQuery,
} from '@/apis/extended/admin/agentApi';
import { useGetUsersByAdminQuery } from '@/apis/extended/admin/userApi';
import { useGetAllContractsForAdminQuery } from '@/apis/extended/contractApi';
import { LinkSellerForm } from '@/components/Admin/LinkSellerForm';
import { ManageAgentTable } from '@/components/Admin/ManageAgent/ManageAgentTable';
import { SortableTableCell } from '@/components/Common/SortableTableCell';
import { AddAgentModal } from '@/components/ConfirmModals/AddAgentModal';
import { useAgentNameById } from '@/hooks/useAgentNameById';
import { useTableSort } from '@/hooks/useTableSort';
import { getContractOfferPrice } from '@/utils/contractOfferPrice';
import { getContractPropertyAddress } from '@/utils/contractPropertyAddress';

export const AdminAgent = () => {
  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false);
  const [contractId, setContractId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [assignmentMessage, setAssignmentMessage] = useState<string | null>(
    null
  );

  const { data: agentsData } = useGetAgentsByAdminQuery({
    offset: 0,
    limit: 1000,
  });
  const { data: usersData } = useGetUsersByAdminQuery({
    offset: 0,
    limit: 1000,
  });
  const [assignAgentToContract, { isLoading: isAssigning }] =
    useAssignAgentToContractMutation();
  const { data: contracts, refetch: refetchContracts } =
    useGetAllContractsForAdminQuery();
  const [assignmentSeverity, setAssignmentSeverity] = useState<
    'success' | 'warning' | 'error' | 'info'
  >('info');

  const agentOptions = useMemo(() => {
    return (agentsData?.agents || []).map((agent: any) => ({
      value: agent._id,
      label: agent.name || agent.email || agent._id,
    }));
  }, [agentsData]);

  const agentNameById = useAgentNameById();

  /** Contracts to pick from — replaces pasting a raw contract ID. */
  const contractOptions = useMemo(
    () =>
      (contracts || []).map((c: any) => {
        const address =
          getContractPropertyAddress(c).addressLine || 'Address not set';
        const price = getContractOfferPrice(c);
        return {
          id: String(c._id),
          address,
          buyer: c.buyerInfo?.name || c.buyer?.name || 'Buyer not set',
          price: price
            ? `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
            : '',
          status: c.contractStatus || 'draft',
          assignedTo: c.agentIdentity
            ? agentNameById[String(c.agentIdentity)] || 'another agent'
            : null,
          seller:
            c.sellerInfo?.name ||
            c.sellerInfo?.email ||
            c.agentSellerInfo?.sellerName ||
            c.agentSellerInfo?.sellerEmail ||
            null,
        };
      }),
    [contracts, agentNameById]
  );
  const selectedContract =
    contractOptions.find((o) => o.id === contractId) || null;

  const sellers = useMemo(() => {
    return (usersData?.users || []).filter(
      (user: any) => user.role === USER_ROLE.SELLER
    );
  }, [usersData]);
  const sellerSort = useTableSort<any>(sellers, {
    name: (s) => s.name,
    email: (s) => s.email,
    agentEmail: (s) => s.latestSellerAgentEmail,
    properties: (s) => s.propertyCount ?? 0,
  });

  const handleAssignAgent = async () => {
    if (!contractId || !selectedAgentId) {
      setAssignmentSeverity('warning');
      setAssignmentMessage('Choose an agent and a contract.');
      return;
    }
    setAssignmentMessage(null);
    try {
      const result = await assignAgentToContract({
        agentId: selectedAgentId,
        contractId,
      }).unwrap();
      setAssignmentSeverity(
        result?.emailSent === false ? 'warning' : 'success'
      );
      setAssignmentMessage(result?.message || 'Agent assigned.');
      setContractId('');
      setSelectedAgentId('');
      refetchContracts();
    } catch (error: any) {
      setAssignmentSeverity('error');
      setAssignmentMessage(
        error?.data?.message || 'Failed to assign agent to contract.'
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h2" sx={{ mb: 1 }}>
          Assign Agent to Contract
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The agent is emailed the property, purchase price, closing date and
          contingencies. Edit that email under Emails → agent-assigned.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            select
            label="Agent"
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            fullWidth
          >
            {agentOptions.map((agent) => (
              <MenuItem key={agent.value} value={agent.value}>
                {agent.label}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={contractOptions}
            value={selectedContract}
            onChange={(_, option) => setContractId(option?.id || '')}
            getOptionLabel={(o) => `${o.address} — ${o.buyer}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return opts;
              return opts.filter((o) =>
                [o.address, o.buyer, o.id, o.assignedTo || '']
                  .join(' ')
                  .toLowerCase()
                  .includes(q)
              );
            }}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {o.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.buyer}
                    {o.price ? ` · ${o.price}` : ''} · {o.status}
                    {o.assignedTo
                      ? ` · assigned to ${o.assignedTo}`
                      : ' · unassigned'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contract"
                placeholder="Search by address, buyer or contract ID"
                helperText={
                  selectedContract?.assignedTo
                    ? `Currently assigned to ${selectedContract.assignedTo} — assigning will reassign it.`
                    : ' '
                }
              />
            )}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleAssignAgent}
            disabled={isAssigning}
            sx={{ height: 'fit-content' }}
          >
            Assign
          </Button>
        </Box>
        {assignmentMessage && (
          <Alert severity={assignmentSeverity} sx={{ mt: 2 }}>
            {assignmentMessage}
          </Alert>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: {
            xs: 'wrap',
            sm: 'nowrap',
          },
        }}
      >
        <Typography variant="h1">Agents</Typography>
        <Button
          sx={{ width: { xs: '100%', sm: 'unset' } }}
          endIcon={<i className="fa-light fa-plus" />}
          onClick={() => setIsAddAgentModalOpen(true)}
        >
          Add Agent
        </Button>
      </Box>
      <Box mt={3}>
        <ManageAgentTable />

        <Box sx={{ mt: 4 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Sellers
          </Typography>
          <LinkSellerForm
            contractOptions={contractOptions}
            onLinked={() => refetchContracts()}
          />
          {sellers.length === 0 ? (
            <Alert severity="info">
              No sellers yet — link one to a contract above.
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableCell sortKey="name" sort={sellerSort}>
                      Name
                    </SortableTableCell>
                    <SortableTableCell sortKey="email" sort={sellerSort}>
                      Email
                    </SortableTableCell>
                    <SortableTableCell sortKey="agentEmail" sort={sellerSort}>
                      Agent Email
                    </SortableTableCell>
                    <SortableTableCell sortKey="properties" sort={sellerSort}>
                      Properties
                    </SortableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellerSort.sortedRows.map((seller: any) => (
                    <TableRow key={seller._id}>
                      <TableCell>{seller.name || '—'}</TableCell>
                      <TableCell>{seller.email || '—'}</TableCell>
                      <TableCell>
                        {seller.latestSellerAgentEmail || '—'}
                      </TableCell>
                      <TableCell>{seller.propertyCount ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
      <AddAgentModal
        isOpen={isAddAgentModalOpen}
        onClose={() => setIsAddAgentModalOpen(false)}
      />
    </Box>
  );
};
